import MenuFirstLevel, { IFirstLevelItem } from '@components/Header/Menu/components/MenuFirstLevel'
import MenuSecondLevel, {
  ISecondLevelItem,
  ISecondLevelProduct,
} from '@components/Header/Menu/components/MenuSecondLevel'
import { useBlogListQuery } from '@graphql/queries/__generated__/blog.generated'
import {
  useCategoryListLazyQuery,
  useCategoryListRandomSpecificsQuery,
  useMainCategoryListQuery,
} from '@graphql/queries/__generated__/category.generated'
import { useHomeArticlesLazyQuery } from '@graphql/queries/__generated__/home.generated'
import {
  useSalesQuery,
  useSalesWithCategoriesAndSpecificsLazyQuery,
} from '@graphql/queries/__generated__/sales.generated'
import Hidden from '@mui/material/Hidden'
import Skeleton from '@mui/material/Skeleton'
import makeStyles from '@mui/styles/makeStyles'
import _ from 'lodash'
import React, { useEffect, useMemo } from 'react'
import { DEFAULT_CURRENCY, FEATURES } from '../../../../constants'
import { DiscountTypeEnum } from '../../../../generated/gqlTypes'
import {
  getBlogArticleUrl,
  getBlogCategoryUrl,
  getBlogUrl,
  getCategoryUrl,
  getProductUrl,
  getSearchUrl,
  WHOLESALE_ROUTES,
} from '../../../urls'
import { maybe } from '../../../utils/misc'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../../../apolloClient'
import Loader from '@components/Loader'
import { useTranslation } from 'react-i18next'

const MENU_LEVEL_CUSTOM_ID = {
  WHOLESALE: 'wholesale',
  SALE: 'sale',
}

const useStyles = makeStyles(() => ({
  itemImg: {
    height: 300,
    width: '100%',
    '& img': {
      height: '100%',
      objectFit: 'contain',
      width: '100%',
    },
  },
  skeleton: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 10,
    '& > span': {
      margin: '0 5px',
    },
  },
  utilitiesItem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignItems: 'center',
    '& svg': {
      marginTop: 5,
    },
    '& svg:last-of-type': {
      display: 'none',
    },
    '&:hover': {
      '& svg:last-of-type': {
        display: 'block',
      },
      '& svg:first-of-type': {
        display: 'none',
      },
    },
  },
  articlePlaceholder: {
    paddingBottom: '70%',
  },
  productPlaceholder: {
    paddingBottom: '125%',
  },
}))

const MenuSkeleton = () => {
  const classes = useStyles()
  return (
    <div className={classes.skeleton}>
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
      <Skeleton variant='text' width={_.random(100, 250)} height={20} />
    </div>
  )
}

const Menu: React.FC<{
  setIsActiveOverlay(val: boolean): void
}> = ({ setIsActiveOverlay }) => {
  const { t } = useTranslation()
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const [activeFirstLevelId, setActiveFirstLevelId] = React.useState<string>(null)

  const dataCategories = useMainCategoryListQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const [loadAllCategories, dataCategoriesAll] = useCategoryListLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const dataCategoriesSpecifics = useCategoryListRandomSpecificsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    skip: !dataCategories.data || !activeFirstLevelId,
  })
  const { data: blogListData } = useBlogListQuery({
    variables: { first: 1, isWholesale: isWholesaleMode },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const [loadArticles, articlesQueryResult] = useHomeArticlesLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      first: 20,
    },
  })
  const [loadSalesWithCategories, salesWithCategoriesQueryResult] = useSalesWithCategoriesAndSpecificsLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const salesQuery = useSalesQuery({ fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first' })
  useEffect(() => {
    setIsActiveOverlay(!!activeFirstLevelId && ![MENU_LEVEL_CUSTOM_ID.WHOLESALE].includes(activeFirstLevelId))
    if (!!activeFirstLevelId && !articlesQueryResult.data) {
      void loadAllCategories()
      void loadArticles()
      void loadSalesWithCategories()
    }
  }, [activeFirstLevelId])

  const firstLevelLinks = useMemo<IFirstLevelItem[]>(() => {
    let links: IFirstLevelItem[] = []
    dataCategories.data?.categories
      .filter(cat => (isWholesaleMode ? cat.isWholesale : true))
      .forEach(c => {
        links = [...links, { id: c.id, name: c.name, pathname: getCategoryUrl(c.slug, c.pk) }]
      })

    blogListData?.blogList.edges.forEach(edge => {
      links = [...links, { id: edge.node.id, name: edge.node.name, pathname: getBlogUrl(edge.node.slug) }]
    })
    if (FEATURES.WHOLESALE) {
      links.push({
        id: MENU_LEVEL_CUSTOM_ID.WHOLESALE,
        name: t('wholesale.menu.wholesale', 'Wholesale'),
        pathname: WHOLESALE_ROUTES.HOME,
      })
    }
    if (salesQuery.data?.saleList.edges.length > 0 && !isWholesaleMode) {
      links = [
        ...links,
        {
          id: MENU_LEVEL_CUSTOM_ID.SALE,
          name: t('sale', 'Sale'),
          pathname: `${getSearchUrl(
            '',
            salesQuery.data.saleList.edges.map(edge => edge.node.pk)
          )}&sortBy=most_recent&sortByDirection=DESC`,
          className: 'menu-sale',
        },
      ]
    }

    return links
  }, [dataCategories.data, blogListData, salesQuery.data, isWholesaleMode, t])

  const secondLevelItems = useMemo(() => {
    const items: { id: string; hideLoader?: boolean; items: ISecondLevelItem[]; products: ISecondLevelProduct[] }[] = []

    dataCategoriesAll.data?.categories
      .filter(cat => (isWholesaleMode ? cat.isWholesale : true))
      .forEach((node, idx) => {
        const children = node.children
        const itemData = {
          id: node.id,
          items: children.map(c => ({
            id: c.id,
            name: c.name,
            pathname: getCategoryUrl(node.slug, node.pk, [{ id: c.pk, slug: c.slug }]),
            className: '',
            onClick() {
              setActiveFirstLevelId(node.id)
            },
            children: c.children.map(child => ({
              id: child.id,
              name: child.name,
              pathname: getCategoryUrl(node.slug, node.pk, [{ id: child.pk, slug: child.slug }]),
              onClick() {
                setActiveFirstLevelId(node.id)
              },
            })),
          })),
          products: [],
        }

        if (salesWithCategoriesQueryResult.data?.saleList.edges.length) {
          const salesInCat: number[] = []
          salesWithCategoriesQueryResult.data.saleList.edges.forEach(edge => {
            if (edge.node.categories.map(c => c.id).includes(node.id)) {
              salesInCat.push(edge.node.pk)
            }
          })
          if (salesInCat.length > 0 && !isWholesaleMode) {
            itemData.items.push({
              id: `sale_${idx}`,
              name: 'Sale',
              pathname: `${getCategoryUrl(node.slug, node.pk)}?sale=${_.uniq(salesInCat).join(
                ','
              )}&sortBy=most_recent&sortByDirection=DESC`,
              onClick() {
                setActiveFirstLevelId(node.id)
              },
              children: [],
              className: 'text-red',
            })
          }
        }
        itemData.products = _.flatMap(
          dataCategoriesSpecifics.data?.categories
            .find(cat => cat.id === node.id)
            ?.randomSpecifics.edges.map(({ node: p }) => ({
              id: p.id,
              name: p.shortName || p.name,
              pathname: getProductUrl(p.slug, p.pk),
              image: p.product?.thumbnailUrl,
            }))
        )
        items.push(itemData)
      })

    if (!dataCategoriesAll.data?.categories?.length) {
      dataCategories.data?.categories.forEach(node => {
        items.push({ id: node.id, items: [], products: [] })
      })
    }
    if (blogListData?.blogList.edges.length > 0) {
      blogListData?.blogList.edges
        .filter(edge => edge.node.isWholesale === isWholesaleMode)
        .forEach(edge => {
          const blogCategoriesItems: ISecondLevelItem[] = []
          const node = edge.node
          node.categories.map(c => {
            blogCategoriesItems.push({
              id: c.id,
              name: c.name,
              pathname: getBlogCategoryUrl(node.slug, c.slug),
              onClick() {
                setActiveFirstLevelId(node.id)
              },
            })
          })
          const articles = maybe(
            () =>
              _.sampleSize(
                articlesQueryResult.data.blogArticleList.edges
                  .map(edge => edge.node)
                  .filter(node_ => node_.categories.length > 0 && node_.categories[0].blog.id === node.id),
                4
              ),
            []
          )
          const blogArticles = articles.map(article => ({
            id: article.id,
            name: article.title,
            pathname: getBlogArticleUrl(node.slug, article.slug),
            image: article.mediumThumbnailWebp,
            placeholderPaddingBottom: '70%',
          }))
          items.push({ id: node.id, items: blogCategoriesItems, products: blogArticles })
        })
    }
    if (FEATURES.WHOLESALE) {
      items.push({ id: _.uniqueId(), items: [], products: [], hideLoader: true }) // Wholesale link
    }
    if (salesWithCategoriesQueryResult.data?.saleList.edges.length > 0) {
      const saleItems: ISecondLevelItem[] = [
        {
          id: 'limited_time_offers',
          name: 'Limited Time Offers',
          pathname: getSearchUrl(
            '',
            salesWithCategoriesQueryResult.data.saleList.edges.map(edge => edge.node.pk)
          ),
          onClick() {
            setActiveFirstLevelId(MENU_LEVEL_CUSTOM_ID.SALE)
          },
          children: salesWithCategoriesQueryResult.data.saleList.edges.map(edge => {
            const node = edge.node
            return {
              id: node.id,
              name: `${node.name} -${node.value}${
                node.type === DiscountTypeEnum.Percentage ? '%' : DEFAULT_CURRENCY
              } (ends on ${new Date(node.availableTo).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
              })})`,
              pathname: getSearchUrl('', [node.pk]),
              onClick() {
                setActiveFirstLevelId(MENU_LEVEL_CUSTOM_ID.SALE)
              },
              children: [],
              className: 'text-red',
            }
          }),
        },
      ]
      const categories = {}
      salesWithCategoriesQueryResult.data.saleList.edges.forEach(saleEdge => {
        saleEdge.node.categories.forEach(category => {
          if (!category.parent) {
            const sales = categories[category.id]
              ? _.uniq([...categories[category.id].sales, saleEdge.node.pk])
              : [saleEdge.node.pk]
            categories[category.id] = {
              id: category.id,
              name: category.name,
              pathname: `${getCategoryUrl(category.slug, category.pk)}?sale=${sales.join(
                ','
              )}&sortBy=most_recent&sortByDirection=DESC`,
              onClick() {
                setActiveFirstLevelId(MENU_LEVEL_CUSTOM_ID.SALE)
              },
              children: categories[category.id] ? categories[category.id].children : [],
              sales: sales,
            }
          } else {
            const parentCategory = saleEdge.node.categories.find(node => node.id === category.parent.id)
            categories[category.parent.id]?.children.push({
              id: category.id,
              name: category.name,
              pathname: `${getCategoryUrl(parentCategory.slug, parentCategory.pk, [
                { slug: category.slug, id: category.pk },
              ])}&sale=${saleEdge.node.pk}&sortBy=most_recent&sortByDirection=DESC`,
              onClick() {
                setActiveFirstLevelId(MENU_LEVEL_CUSTOM_ID.SALE)
              },
              children: [],
              sales: [saleEdge.node.pk],
            })
            if (categories[category.parent.id]) {
              categories[category.parent.id].children = _.uniqBy(
                categories[category.parent.id].children,
                (c: ISecondLevelItem) => c.id
              )
            }
          }
        })
      })
      const saleCategories: ISecondLevelItem[] = Object.values(categories)
      const products = _.sampleSize(
        salesWithCategoriesQueryResult.data?.specifics.edges.map(edge => ({
          id: edge.node.id,
          name: edge.node.shortName || edge.node.name,
          pathname: getProductUrl(edge.node.slug, edge.node.pk),
          image: edge.node?.product?.thumbnail.lg,
          priceRange: edge.node.product?.priceRange,
        })),
        3
      )
      items.push({ id: MENU_LEVEL_CUSTOM_ID.SALE, items: [...saleItems, ...saleCategories], products: products })
    }
    if (items.length < firstLevelLinks.length) {
      _.range(firstLevelLinks.length - items.length).forEach(() => {
        items.push({ id: _.uniqueId(), items: [], products: [] })
      })
    }

    return items.filter(level => !(level.hideLoader && !level.items.length))
  }, [
    dataCategoriesAll.data,
    dataCategories.data,
    blogListData,
    salesWithCategoriesQueryResult.data,
    dataCategoriesSpecifics,
    articlesQueryResult.data,
    firstLevelLinks,
  ])

  return (
    <React.Fragment>
      <Hidden lgDown>
        <div
          className='menu'
          id='home-menu'
          onMouseLeave={() => {
            setActiveFirstLevelId(null)
          }}
        >
          <div className='firstLevel'>
            <ul className='firstLinks'>
              {!dataCategories.data && dataCategories.loading && <MenuSkeleton />}
              <MenuFirstLevel
                firstLevelLinks={firstLevelLinks}
                activeFirstLevelId={activeFirstLevelId}
                setActiveFirstLevelId={setActiveFirstLevelId}
              />
            </ul>
            {secondLevelItems.map(level => {
              return (
                <div key={level.id} className={`secondLevel ${activeFirstLevelId === level.id ? 'active' : ''}`}>
                  <div className='py-10'>
                    <Loader
                      className='spinner'
                      type='ThreeDots'
                      color='#333'
                      visible={
                        dataCategoriesAll.loading ||
                        articlesQueryResult.loading ||
                        salesWithCategoriesQueryResult.loading
                      }
                      height={100}
                      width={100}
                    />
                    <div className='justify-content-center'>
                      <ul className='secondLinks'>
                        <li className='menuLv2 menuCol'>
                          <MenuSecondLevel items={level.items} products={level.products} />
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Hidden>
    </React.Fragment>
  )
}

export default Menu
